import { DateTime } from 'luxon';
import { IlogisticServiceDetails, LOCATION_PLACE_TYPE } from '../types/services';
import { formatShortDate } from './formatDatesAndHours';
import { additionalDaysForUnassignmentCollectionDate } from './productDefinitions';

const formatLocationName = (place: string, countryName: string) => {
  switch (place) {
    case LOCATION_PLACE_TYPE.NUDOS:
      return `Bodega ${countryName}`;
    case LOCATION_PLACE_TYPE.DIAGNOSTIC:
      return 'Diagnostico';
    case LOCATION_PLACE_TYPE.WARRANTY:
      return 'Garantía';
    case LOCATION_PLACE_TYPE.USER:
      return 'Empleado';
    case LOCATION_PLACE_TYPE.OFFICE:
      return `Oficinas ${countryName}`;
    default:
      return 'Sin Ubicación';
  }
};

const formatTools = (generalData: IlogisticServiceDetails) => {
  const logisticServiceDetails = generalData;
  let stringToolsList = '';
  if (logisticServiceDetails?.tools && logisticServiceDetails?.tools?.length > 0) {
    const tools = logisticServiceDetails.tools;
    for (let i = 0; i < tools.length; i++) {
      const detailsTool = tools[i];
      const toolPills = detailsTool.pills;
      const filterPills = Object.fromEntries(
        Object.entries(toolPills || {}).filter(([key]) => !key.includes('Perfil') && !key.includes('Cargo'))
      );
      const validatePillsArray = Object.values(filterPills);
      const toolsStringPills = `${i + 1}.- ${detailsTool.name} (Cargo ${
        toolPills?.['Cargo'] || 'Sin Cargo'
      }, ${validatePillsArray.toString()}) Serial: ${detailsTool?.serial || 'Sin Serial'} \n`;
      stringToolsList = stringToolsList + toolsStringPills;
    }
    return stringToolsList;
  }
  return stringToolsList;
};

const getInitialEstimatedDatesData = (logisticServiceData: IlogisticServiceDetails) => {
  const { collectionInfo, deliveryInfo, estimatedDeliveryTime } = logisticServiceData;
  const estimatedDeliveryText = `${estimatedDeliveryTime?.since || ''} ${
    estimatedDeliveryTime?.until ? `- ${estimatedDeliveryTime?.until}` : ''
  } días hábiles`;
  const estimatedUpperLimitForChosenCollectionDate = DateTime.fromISO(collectionInfo?.chosenCollectionDate || '')
    .plus({ days: additionalDaysForUnassignmentCollectionDate })
    .toISODate();
  const estimatedUpperLimitForChosenDeliveryDate = DateTime.fromISO(deliveryInfo?.chosenDeliveryDate || '')
    .plus({ days: additionalDaysForUnassignmentCollectionDate })
    .toISODate();
  const formatedLowerLimitCollection = formatShortDate(collectionInfo?.chosenCollectionDate || '')?.onlyDate || '';
  const formatedUpperLimitCollection = `- ${
    formatShortDate(estimatedUpperLimitForChosenCollectionDate || '')?.onlyDate || ''
  }`;
  const formatedLowerLimitDelivery = formatShortDate(deliveryInfo?.chosenDeliveryDate || '')?.onlyDate || '';
  const formatedUpperLimitDelivery = `- ${
    formatShortDate(estimatedUpperLimitForChosenDeliveryDate || '')?.onlyDate || ''
  }`;

  if (collectionInfo?.chosenCollectionDate) {
    return {
      collectionDatesText: `${formatedLowerLimitCollection} ${formatedUpperLimitCollection}`,
      estimatedDeliveryText
    };
  }
  if (deliveryInfo?.chosenDeliveryDate) {
    const choseDeliveryDateText = `${formatedLowerLimitDelivery} ${formatedUpperLimitDelivery}`;
    return { collectionDatesText: undefined, estimatedDeliveryText: choseDeliveryDateText };
  }
  return { collectionDatesText: undefined, estimatedDeliveryText };
};

const copyInfoLS = async (generalData: IlogisticServiceDetails) => {
  const dates = getInitialEstimatedDatesData(generalData);
  const logisticServiceDetails = generalData;
  const numberLS = logisticServiceDetails?.id;
  const extraInfo = logisticServiceDetails?.collectionInfo?.collectionComment || 'Sin información adicional';
  const originInfo = {
    placeName: formatLocationName(
      logisticServiceDetails?.originLocation?.place || '',
      logisticServiceDetails?.originLocation?.country?.name || ''
    ),
    delivererName: logisticServiceDetails?.collectionInfo?.delivererName || 'Sin Nombre',
    cityAndCountry:
      logisticServiceDetails?.originLocation?.place !== 'nudos'
        ? `${logisticServiceDetails?.originLocation?.city || ''}, ${
            logisticServiceDetails?.originLocation?.country?.name
          }\n`
        : '',
    address:
      logisticServiceDetails?.originLocation?.place === 'nudos'
        ? `Bodegas ${logisticServiceDetails?.originLocation?.country?.name}`
        : logisticServiceDetails?.originLocation?.address,
    zipCode: logisticServiceDetails?.originLocation?.zipCode || 'No disponible'
  };

  const destinationInfo = {
    placeName: formatLocationName(
      logisticServiceDetails?.destinationLocation?.place || '',
      logisticServiceDetails?.destinationLocation?.country?.name || ''
    ),
    receiverName: logisticServiceDetails?.deliveryInfo?.receiverName || 'Sin Nombre',
    cityAndCountry:
      logisticServiceDetails?.destinationLocation?.place !== 'nudos'
        ? `${logisticServiceDetails?.destinationLocation?.city || ''}, ${
            logisticServiceDetails?.destinationLocation?.country?.name
          }\n`
        : '',
    address:
      logisticServiceDetails?.destinationLocation?.place === 'nudos'
        ? `Bodegas ${logisticServiceDetails?.destinationLocation?.country?.name}`
        : logisticServiceDetails?.destinationLocation?.address,
    zipCode: logisticServiceDetails?.destinationLocation?.zipCode || 'No disponible'
  };

  const tools = formatTools(generalData);
  const copyText = `*Solicitud de servicio logístico N°${numberLS}*\nSolicitamos la confirmación, ETA e información y link de rastreo del siguiente servicio logístico:\n*Detalle del servicio N°${numberLS}*\n⚠️ *_Comentarios e información a considerar_*\n*${extraInfo}*\n*_Ubicaciones_*\nEstas son las ubicaciones proporcionadas por el cliente. Verifica que se encuentren correctas:\n*_Origen: ${originInfo.placeName}_*\n${originInfo.delivererName}\n${originInfo?.address}\nCP: ${originInfo.zipCode}\n*_Destino: ${destinationInfo.placeName}_*\n${destinationInfo.receiverName}\n${destinationInfo?.cityAndCountry}${destinationInfo.address}\nCP: ${destinationInfo.zipCode}\n*_Fechas solicitadas_*\nEste es el rango de fechas solicitadas por el cliente, confírmanos que sean viables:\nESTIMADO DE *RECOLECCIÓN EN PUNTO DE ORIGEN*\n${dates.estimatedDeliveryText}\n*_Herramienta(s)_*\nEsta es la lista de herramientas a trasladar:\n${tools}`;
  await navigator.clipboard.writeText(copyText);
};

export { copyInfoLS };
